import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useQueries } from 'react-query';
import { useSelector, useDispatch } from 'react-redux';
import { Snackbar } from '@nextiva/next-ui-components';

import { getTeams, getUsersBasicInfo } from 'api/iamOrchestrationApi';
import {
  SearchBarWrapper,
  SearchBar,
  TeamTableHeader,
  TeamList,
  PermissionNeeded,
} from 'components';
import DeleteVoiceModal from 'components/DeleteVoiceModal';
import { FullScreenBackdrop } from 'components/FullScreenBackdrop';
import { CREATE_TEAM, EDIT_TEAM } from 'constants/drawerTitles';
import { Page } from 'containers';
import Drawer from 'containers/Drawer';
import { screens } from 'containers/pages/DataImport';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import { VOICE_ADMIN_DISABLE_DUETO_BWPATCHING } from 'constants/messageText';
import { useGetInfo, useSnackbar } from 'hooks';
import { isAdminAndVoiceUser } from 'utils/user-identifier';
import CreateTeam from './CreateTeam';
import { actionTypes } from './redux/actionTypes';

const importDropdownOptions = [{ importName: 'Bulk add new teams' }];
const Teams = () => {
  const dispatch = useDispatch();
  const [showDrawer, setShowDrawer] = useState(false);
  const [drawerTitle, setDrawerTitle] = useState(CREATE_TEAM);
  const [isVoiceAccount, setVoiceAccount] = useState(false);
  const [primaryIdentifier, setPrimaryIdentifier] = useState(false);
  const [noPermission, setNoPermission] = useState(false);
  const teamsState = useSelector((state) => state.teamsReduxReducer);

  const setSnackbar = useSnackbar();
  const { data: user } = useGetInfo();
  const [
    { isLoading: teamsLoading, data: teamsData, error: teamsError },
    {
      isLoading: usersInfoLoading,
      data: usersBasicInfo,
      isError: isUsersInfoError,
    },
  ] = useQueries([
    { queryKey: 'getTeams', queryFn: getTeams },
    { queryKey: 'getUsersBasicInfo', queryFn: getUsersBasicInfo },
  ]);

  const {
    featureState: { authV3Enabled, isClusterIdInBWPatching, bulkImportsTeams },
  } = useContext(FeatureContext);

  const openTeamDrawer = useCallback((e) => {
    if (e) {
      setDrawerTitle(CREATE_TEAM);
    } else {
      setDrawerTitle(EDIT_TEAM);
    }
    setShowDrawer((f) => !f);
  }, []);

  const toggleTeamDrawer = useCallback(() => {
    setShowDrawer((e) => !e);
  }, []);

  const searchTeamData = useCallback((searchTerm) => {
    dispatch({
      type: actionTypes.FILTER_VIEW_LIST,
      searchTerm,
    });
  });

  const getTeamsList = () => {
    dispatch({
      type: actionTypes.SET_TEAMS_LIST_IS_LOADING,
      teamsListIsLoading: false,
    });

    if (teamsError) {
      setNoPermission(teamsError.status === 403);

      setSnackbar({
        message:
          'There was an error while retrieving the list of teams. Please try again or contact support if the issue persists.',
        severity: 'error',
      });

      return;
    }

    dispatch({
      type: actionTypes.SET_BASE_LIST,
      teamsList: teamsData,
    });
  };

  const downloadTeamsCSV = () => {
    const csvDownloadLink = document.createElement('a');
    const csvHeader = ['name', 'userCount', 'phoneNumber', 'extension'].join(
      ',',
    );
    const csvBody = teamsState.viewList
      .map((team) =>
        [
          JSON.stringify(team.name),
          team.userCount,
          team.phoneNumber,
          team.extension,
        ].join(','),
      )
      .join('\n');
    const csvData = `${csvHeader}\n${csvBody}`;
    const file = new Blob([csvData], { type: 'text/plain' });
    csvDownloadLink.href = URL.createObjectURL(file);
    csvDownloadLink.download = `teams.csv`;
    csvDownloadLink.click();
  };

  useEffect(() => {
    if (teamsLoading || usersInfoLoading) {
      dispatch({
        type: actionTypes.SET_TEAMS_LIST_IS_LOADING,
        teamsListIsLoading: true,
      });
      return;
    }

    getTeamsList();

    if (isClusterIdInBWPatching) {
      (async () => {
        const account = await isAdminAndVoiceUser();

        const identifier = authV3Enabled
          ? Boolean(user?.profileDTO?.nextOSIdent?.primaryIdentifier)
          : Boolean(user?.profiles?.idents?.nextOS?.primary_identifier);
        setVoiceAccount(account);
        setPrimaryIdentifier(identifier);
      })();
    }
  }, [
    teamsLoading,
    teamsData,
    usersInfoLoading,
    isUsersInfoError,
    usersBasicInfo,
  ]);

  if (noPermission) {
    return (
      <Page title="Teams">
        <PermissionNeeded />
      </Page>
    );
  }

  return (
    <Page title="Teams">
      {isClusterIdInBWPatching && (isVoiceAccount || primaryIdentifier) && (
        <div className="banner" style={{ marginBottom: 32 }}>
          <Snackbar
            message={VOICE_ADMIN_DISABLE_DUETO_BWPATCHING}
            variant="info"
            hideAction
          />
        </div>
      )}
      <div className="m-header-search-bar">
        {bulkImportsTeams ? (
          <SearchBarWrapper
            id={screens.TEAMS_IMPORT}
            label="Current Teams"
            placeholder="Search teams"
            searchData={searchTeamData}
            actionName="Add team"
            onClick={openTeamDrawer}
            importDropdownOptions={importDropdownOptions}
            downloadCSV={downloadTeamsCSV}
            data-testid="PlatformAdminUI-Teams-SearchBarWrapper"
          />
        ) : (
          <SearchBar
            id="SearchBarManage"
            label="Current Teams"
            placeholder="Search teams"
            searchData={searchTeamData}
            actionName="Create team"
            onClick={openTeamDrawer}
            downloadCSV={downloadTeamsCSV}
            data-testid="PlatformAdminUI-Teams-SearchBar"
          />
        )}
      </div>
      <TeamTableHeader />
      <TeamList onEditTeam={openTeamDrawer} />

      <Drawer
        title={drawerTitle}
        toggleDrawer={toggleTeamDrawer}
        open={showDrawer}
      >
        <CreateTeam
          toggleDrawer={toggleTeamDrawer}
          type={drawerTitle}
          userRetrieveError={isUsersInfoError}
        />
      </Drawer>
      <FullScreenBackdrop
        message={teamsState.backdrop.message}
        open={teamsState.backdrop.open}
      />
      <DeleteVoiceModal />
    </Page>
  );
};

export default Teams;
